import { useReducer, useEffect, useCallback, ChangeEvent, useRef, useState } from 'react';
import { WfProductDetails } from 'src/api/wf/models/WfProduct';
import { WfFloodCategoryStatus } from 'src/api/wf/models/enums/WfFloodCategoryStatus';
import { getDefaultFloodLocation } from 'src/api/wf/models/enums/WfFloodLocation';
import {
  resolveResponseInputFromApi,
  WfRecoveryStatus,
} from 'src/api/wf/models/enums/WfRecoveryStatus';
import {
  isWfPartArrangementStatus,
  WfPartArrangementStatus,
} from 'src/api/wf/models/enums/WfPartArrangementStatus';
import useWfRecoveryService from 'src/api/wf/service/WfRecoveryService';
import { PhotoItem } from '../WF_4_6_1_PropertyStatusDetailsRegistration';

// --- Form State Management ---

export type FormState = {
  partsRequired: boolean;
  responseInput: string;
  floodCategory: string;
  drainageRequired: boolean;
  pitDepth: number;
  pitWidth: number;
  pitLength: number;
  waterDepth: number;
  priority: number;
  partsNumber: string;
  partsName: string;
  quantity: string;
  arrangement: string;
  estimatedArrivalTime: string;
  requestNumber: string;
  partsComments: string;
  floodLocation: string;
  estimatedWorkTime: number;
  floodComments: string;
  workSubmissionStatus: string;
  workRemarks: string;
  deliveryAddress: string;
  workOrderNumber: string;
  floodRequired: boolean;
};

type FormAction<K extends keyof FormState = keyof FormState> =
  | { type: 'SET_FIELD'; field: K; value: FormState[K] }
  | { type: 'RESET'; payload: FormState };

export const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'RESET':
      return action.payload;
    default:
      return state;
  }
};

export const getInitialFormState = (details: WfProductDetails | null): FormState => {
  const {
    partsRequired,
    responseInput,
    floodCategory: rawFloodCategory,
    drainageRequired,
    pitDepth,
    pitWidth,
    pitLength,
    waterDepth,
    priority: rawPriority,
    partsNumber,
    partsName,
    quantity,
    arrangement,
    estimatedArrivalTime,
    requestNumber,
    partsComments: rawPartsComments,
    floodedLocation: rawFloodedLocation,
    estimatedWorkTime,
    floodComments: rawFloodComments,
    workSubmissionStatus,
    workRemarks: rawWorkRemarks,
    deliveryAddress,
    workOrderNumber,
    floodRequired,
  } = details ?? {};

  return {
    partsRequired: partsRequired ?? false,
    responseInput: resolveResponseInputFromApi(responseInput),
    floodCategory: rawFloodCategory ?? WfFloodCategoryStatus.EL,
    drainageRequired: drainageRequired ?? false,
    pitDepth: pitDepth ?? 0,
    pitWidth: pitWidth ?? 0,
    pitLength: pitLength ?? 0,
    waterDepth: waterDepth ?? 0,
    priority: rawPriority ?? 0,
    partsNumber: partsNumber ?? '',
    partsName: partsName ?? '',
    quantity: quantity ?? '',
    arrangement: isWfPartArrangementStatus(arrangement)
      ? arrangement
      : WfPartArrangementStatus.NotArranged,
    estimatedArrivalTime: estimatedArrivalTime ?? '',
    requestNumber: requestNumber ?? '',
    partsComments: rawPartsComments ?? '',
    floodLocation:
      rawFloodedLocation ??
      getDefaultFloodLocation(
        (rawFloodCategory as WfFloodCategoryStatus) ?? WfFloodCategoryStatus.EL
      ),
    estimatedWorkTime: estimatedWorkTime ?? 0,
    floodComments: rawFloodComments ?? '',
    workSubmissionStatus: workSubmissionStatus ?? '',
    workRemarks: rawWorkRemarks ?? '',
    deliveryAddress: deliveryAddress ?? '',
    workOrderNumber: workOrderNumber ?? '',
    floodRequired: floodRequired ?? false,
  };
};

export const useFormState = (productDetails: WfProductDetails | null) => {
  const initialState = getInitialFormState(productDetails);
  const [formState, dispatch] = useReducer(formReducer, initialState);

  const handleFieldChange = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      dispatch({ type: 'SET_FIELD', field, value });

      // When responseInput changes to non-FloodingResponsePartReplacementRequired,
      // clear all flood and parts related fields
      if (
        field === 'responseInput' &&
        value !== WfRecoveryStatus.FloodingResponsePartReplacementRequired
      ) {
        dispatch({ type: 'SET_FIELD', field: 'floodRequired', value: false as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'partsRequired', value: false as FormState[K] });
        dispatch({
          type: 'SET_FIELD',
          field: 'floodCategory',
          value: WfFloodCategoryStatus.EL as FormState[K],
        });
        dispatch({ type: 'SET_FIELD', field: 'drainageRequired', value: false as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'pitDepth', value: 0 as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'pitWidth', value: 0 as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'pitLength', value: 0 as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'waterDepth', value: 0 as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'priority', value: 0 as FormState[K] });
        dispatch({
          type: 'SET_FIELD',
          field: 'floodLocation',
          value: getDefaultFloodLocation(WfFloodCategoryStatus.EL) as FormState[K],
        });
        dispatch({ type: 'SET_FIELD', field: 'estimatedWorkTime', value: 0 as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'floodComments', value: '' as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'partsNumber', value: '' as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'partsName', value: '' as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'quantity', value: '' as FormState[K] });
        dispatch({
          type: 'SET_FIELD',
          field: 'arrangement',
          value: WfPartArrangementStatus.NotArranged as FormState[K],
        });
        dispatch({
          type: 'SET_FIELD',
          field: 'estimatedArrivalTime',
          value: '' as FormState[K],
        });
        dispatch({ type: 'SET_FIELD', field: 'requestNumber', value: '' as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'partsComments', value: '' as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'deliveryAddress', value: '' as FormState[K] });
        dispatch({ type: 'SET_FIELD', field: 'workOrderNumber', value: '' as FormState[K] });
      }
    },
    []
  );

  const serializedProductDetails = JSON.stringify(productDetails);
  const resetForm = useCallback(() => {
    const details = JSON.parse(serializedProductDetails) as WfProductDetails | null;
    dispatch({ type: 'RESET', payload: getInitialFormState(details) });
  }, [serializedProductDetails]);

  useEffect(() => {
    const details = JSON.parse(serializedProductDetails) as WfProductDetails | null;
    dispatch({ type: 'RESET', payload: getInitialFormState(details) });
  }, [serializedProductDetails]);

  return { formState, handleFieldChange, resetForm };
};

// --- Photo State Management ---

const PHOTO_DATA_PREFIX = 'data:image/jpeg;base64,';
// 3 photos or fewer are small enough to auto-download; 4+ stay collapsed until tapped.
const AUTO_DISPLAY_PHOTO_THRESHOLD = 3;

type PhotoRowsAction =
  | { type: 'SET'; payload: PhotoItem[][] }
  | { type: 'ADD_ROW' }
  | { type: 'ADD_PHOTOS'; rowIndex: number; photos: PhotoItem[] }
  | { type: 'REPLACE_ROW_PHOTO'; rowIndex: number; photo: PhotoItem }
  | { type: 'REMOVE_SELECTED'; rowIndex: number; indicesToRemove: number[] }
  | { type: 'SET_PHOTO_URL'; rowIndex: number; url: string };

export const photoRowsReducer = (state: PhotoItem[][], action: PhotoRowsAction): PhotoItem[][] => {
  switch (action.type) {
    case 'SET':
      return action.payload;
    case 'ADD_ROW':
      if (state.length >= 15) return state;
      return [...state, []];
    case 'ADD_PHOTOS': {
      return state.map((row, index) => {
        if (index !== action.rowIndex) return row;
        if (row.length + action.photos.length > 15) return row;
        return [...row, ...action.photos];
      });
    }
    case 'REPLACE_ROW_PHOTO': {
      return state.map((row, index) => {
        if (index !== action.rowIndex) return row;
        return [action.photo];
      });
    }
    case 'REMOVE_SELECTED': {
      if (action.indicesToRemove.length === 0) return state;
      return state.map((row, index) => {
        if (index !== action.rowIndex) return row;
        return row.filter((_, i) => !action.indicesToRemove.includes(i));
      });
    }
    case 'SET_PHOTO_URL': {
      return state.map((row, index) => {
        if (index !== action.rowIndex || row.length === 0) return row;
        return [{ ...row[0], url: action.url }, ...row.slice(1)];
      });
    }
    default:
      return state;
  }
};

type CheckedPhotosAction =
  | { type: 'SET'; payload: Record<number, number[]> }
  | { type: 'TOGGLE'; rowIndex: number; photoIndex: number }
  | { type: 'RESET_ROW'; rowIndex: number };

export const checkedPhotosReducer = (
  state: Record<number, number[]>,
  action: CheckedPhotosAction
): Record<number, number[]> => {
  switch (action.type) {
    case 'SET':
      return action.payload;
    case 'TOGGLE': {
      const newChecked = { ...state };
      const rowChecks = newChecked[action.rowIndex] ?? [];
      const newRowChecks = rowChecks.includes(action.photoIndex)
        ? rowChecks.filter((i) => i !== action.photoIndex)
        : [...rowChecks, action.photoIndex];
      newChecked[action.rowIndex] = newRowChecks;
      return newChecked;
    }
    case 'RESET_ROW':
      return { ...state, [action.rowIndex]: [] };
    default:
      return state;
  }
};

export const usePhotoManager = (photoIds: string[] | undefined, namePrefix: string) => {
  const serviceFunctions = useWfRecoveryService();
  const [photoRows, dispatchPhotoRows] = useReducer(photoRowsReducer, [[]]);
  const [checkedPhotos, dispatchCheckedPhotos] = useReducer(checkedPhotosReducer, {});
  const [pendingDeletionPhotoIds, setPendingDeletionPhotoIds] = useState<string[]>([]);
  const [loadingRows, setLoadingRows] = useState<Record<number, boolean>>({});
  const initialPhotoRowsRef = useRef<PhotoItem[][]>([[]]);

  const serviceFunctionsRef = useRef(serviceFunctions);

  useEffect(() => {
    serviceFunctionsRef.current = serviceFunctions;
  });

  const serializedPhotoIds = JSON.stringify(photoIds ?? null);

  // Few photos (<= threshold) are auto-downloaded; otherwise only placeholders (id + name) are set.
  useEffect(() => {
    let cancelled = false;
    const applyRows = (rows: PhotoItem[][]) => {
      if (cancelled) return;
      initialPhotoRowsRef.current = rows;
      dispatchPhotoRows({ type: 'SET', payload: rows });
      dispatchCheckedPhotos({ type: 'SET', payload: {} });
      setPendingDeletionPhotoIds([]);
      setLoadingRows({});
    };

    const currentPhotoIds = JSON.parse(serializedPhotoIds) as string[] | undefined;

    if (!currentPhotoIds || currentPhotoIds.length === 0) {
      applyRows([[]]);
      return;
    }

    if (currentPhotoIds.length > AUTO_DISPLAY_PHOTO_THRESHOLD) {
      applyRows(currentPhotoIds.map((id) => [{ id, url: '', name: `${namePrefix}_${id}` }]));
      return;
    }

    const fetchPhoto =
      namePrefix === 'Flood'
        ? serviceFunctionsRef.current.fetchFloodPhoto
        : serviceFunctionsRef.current.fetchPartsPhoto;

    const loadAll = async () => {
      const results = await Promise.all(
        currentPhotoIds.map(async (id) => {
          const response = await fetchPhoto(id);
          return {
            id,
            url: response ? `${PHOTO_DATA_PREFIX}${response}` : '',
            name: `${namePrefix}_${id}`,
          };
        })
      );
      applyRows(results.map((photo) => [photo]));
    };

    loadAll();

    return () => {
      cancelled = true;
    };
  }, [serializedPhotoIds, namePrefix]);

  const loadPhoto = useCallback(
    async (rowIndex: number) => {
      const photo = photoRows[rowIndex]?.[0];
      if (!photo?.id || photo.url || loadingRows[rowIndex]) return;

      const fetchPhoto =
        namePrefix === 'Flood'
          ? serviceFunctionsRef.current.fetchFloodPhoto
          : serviceFunctionsRef.current.fetchPartsPhoto;

      setLoadingRows((prev) => ({ ...prev, [rowIndex]: true }));
      try {
        const response = await fetchPhoto(photo.id);
        if (response) {
          dispatchPhotoRows({
            type: 'SET_PHOTO_URL',
            rowIndex,
            url: `${PHOTO_DATA_PREFIX}${response}`,
          });
        }
      } finally {
        setLoadingRows((prev) => {
          const { [rowIndex]: _removed, ...rest } = prev;
          return rest;
        });
      }
    },
    [photoRows, loadingRows, namePrefix]
  );

  const addRow = useCallback(() => dispatchPhotoRows({ type: 'ADD_ROW' }), []);

  const handlePhotoChange = useCallback(
    (rowIndex: number, event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        const existingRow = photoRows[rowIndex] ?? [];
        const replacedIds = existingRow
          .map((photo) => photo.id)
          .filter((id): id is string => Boolean(id));
        if (replacedIds.length > 0) {
          setPendingDeletionPhotoIds((prev) => Array.from(new Set([...prev, ...replacedIds])));
        }

        // Treat the chooser as single-file input even if a browser returns multiple files.
        const firstFile = event.target.files[0];
        if (!firstFile) {
          event.target.value = '';
          return;
        }
        const newPhotos: PhotoItem[] = [firstFile].map((file) => ({
          url: URL.createObjectURL(file),
          file: file,
          name: file.name,
        }));
        dispatchPhotoRows({ type: 'REPLACE_ROW_PHOTO', rowIndex, photo: newPhotos[0] });
      }
      event.target.value = '';
    },
    [photoRows]
  );

  const togglePhotoCheck = useCallback((rowIndex: number, photoIndex: number) => {
    dispatchCheckedPhotos({ type: 'TOGGLE', rowIndex, photoIndex });
  }, []);

  const resetPhotos = useCallback(() => {
    const initialRows = initialPhotoRowsRef.current;
    dispatchPhotoRows({ type: 'SET', payload: initialRows });
    dispatchCheckedPhotos({ type: 'SET', payload: {} });
    setPendingDeletionPhotoIds([]);
  }, []);

  const clearAllPhotos = useCallback(() => {
    const existingPersistedIds = photoRows
      .flat()
      .map((photo) => photo.id)
      .filter((id): id is string => Boolean(id));

    if (existingPersistedIds.length > 0) {
      setPendingDeletionPhotoIds((prev) => Array.from(new Set([...prev, ...existingPersistedIds])));
    }

    dispatchPhotoRows({ type: 'SET', payload: [[]] });
    dispatchCheckedPhotos({ type: 'SET', payload: {} });
  }, [photoRows]);

  const deleteSelectedPhotos = useCallback(
    (rowIndex: number) => {
      const indicesToDelete = checkedPhotos[rowIndex] || [];
      const photosInRow = photoRows[rowIndex] ?? [];

      const deletionIds = indicesToDelete
        .map((index) => photosInRow[index]?.id)
        .filter((id): id is string => Boolean(id));

      if (deletionIds.length > 0) {
        setPendingDeletionPhotoIds((prev) => Array.from(new Set([...prev, ...deletionIds])));
      }

      dispatchPhotoRows({ type: 'REMOVE_SELECTED', rowIndex, indicesToRemove: indicesToDelete });
      dispatchCheckedPhotos({ type: 'RESET_ROW', rowIndex });
    },
    [checkedPhotos, photoRows]
  );

  const clearPendingDeletionPhotoIds = useCallback(() => {
    setPendingDeletionPhotoIds([]);
  }, []);

  return {
    photoRows,
    checkedPhotos,
    addRow,
    handlePhotoChange,
    togglePhotoCheck,
    deleteSelectedPhotos,
    resetPhotos,
    clearAllPhotos,
    pendingDeletionPhotoIds,
    clearPendingDeletionPhotoIds,
    loadPhoto,
    loadingRows,
  };
};

export type PhotoManager = ReturnType<typeof usePhotoManager>;
export type { PhotoItem };
